/** 项目日志与讨论共用的关键词搜索：搜索全项目历史，避免仅筛选首屏造成漏查。 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";

export function ProjectKeywordInput({ value, onChange, zh, label, testId }) {
  return (
    <div className="relative min-w-0 w-full">
      <Search aria-hidden="true" className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      <input type="text" value={value} onChange={(event) => onChange(event.target.value)} maxLength={120}
        aria-label={label} placeholder={label} data-testid={testId}
        className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-8 pr-9 text-xs text-slate-800 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400" />
      {value && <button type="button" onClick={() => onChange("")} aria-label={zh ? "清除关键词" : "Clear keyword"}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
        <X aria-hidden="true" className="h-3.5 w-3.5" />
      </button>}
    </div>
  );
}

/** 输入防抖并忽略过期请求；分页和原有分类一起发给后端，避免筛选后漏掉更早的匹配项。 */
export function useProjectKeywordSearch({ api, projectId, kind, keyword, filter }) {
  const query = keyword.trim();
  const [revision, setRevision] = useState(0);
  const key = JSON.stringify([projectId, kind, query, filter, revision]);
  const sequence = useRef(0);
  const [state, setState] = useState({ key: "", items: [], loading: false, loadingMore: false, error: "", hasMore: false, nextOffset: 0 });
  const refresh = useCallback(() => setRevision((current) => current + 1), []);

  useEffect(() => {
    const request = ++sequence.current;
    if (!query) return undefined;
    setState({ key, items: [], loading: true, loadingMore: false, error: "", hasMore: false, nextOffset: 0 });
    const timer = setTimeout(async () => {
      try {
        const params = { q: query, offset: 0, [kind === "notes" ? "mode" : "visibility"]: filter };
        const { data } = await api.get(`/projects/${projectId}/hub/${kind}/search`, { params });
        if (sequence.current !== request) return;
        setState({ key, items: data.items || [], loading: false, loadingMore: false, error: "", hasMore: !!data.hasMore, nextOffset: data.nextOffset });
      } catch (error) {
        if (sequence.current !== request) return;
        setState({ key, items: [], loading: false, loadingMore: false, error: error?.response?.data?.detail || error.message || "Search failed", hasMore: false, nextOffset: 0 });
      }
    }, 250);
    return () => { clearTimeout(timer); sequence.current += 1; };
  }, [api, projectId, kind, query, filter, key]);

  const loadMore = useCallback(async () => {
    if (!query || state.key !== key || state.loading || state.loadingMore || !state.hasMore) return;
    const request = sequence.current;
    setState((current) => current.key === key ? { ...current, loadingMore: true, error: "" } : current);
    try {
      const params = { q: query, offset: state.nextOffset, [kind === "notes" ? "mode" : "visibility"]: filter };
      const { data } = await api.get(`/projects/${projectId}/hub/${kind}/search`, { params });
      if (sequence.current !== request) return;
      setState((current) => current.key === key ? { ...current, items: [...current.items, ...(data.items || [])], loadingMore: false, hasMore: !!data.hasMore, nextOffset: data.nextOffset } : current);
    } catch (error) {
      if (sequence.current !== request) return;
      setState((current) => current.key === key ? { ...current, loadingMore: false, error: error?.response?.data?.detail || error.message || "Search failed" } : current);
    }
  }, [api, projectId, kind, query, filter, key, state]);

  const current = state.key === key ? state : { items: [], loading: !!query, loadingMore: false, error: "", hasMore: false };
  return { ...current, active: !!query, refresh, loadMore };
}
